
import { useParams, Link } from 'react-router-dom';
import { events } from '../../../mocks/events';
import { eventDetails } from '../../../mocks/eventDetails';
import { useSEO, generateEventSchema, generateBreadcrumbSchema } from '../../../utils/seo';
import EventMap from './components/EventMap';
import EventReminderSignup from './components/EventReminderSignup';
import PhotoGalleryCarousel from './components/PhotoGalleryCarousel';
import PrintEventFlyerButton from './components/PrintEventFlyerButton';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const eventId = id || '1';

  const event = events.find(e => String(e.id) === eventId);
  const details = eventDetails[eventId];

  // Parse month/day from event date string
  const parseDateParts = (dateStr: string) => {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return {
        month: d.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
        day: String(d.getDate()),
      };
    }
    // Fallback: try to parse from formatted string like "February 21, 2026"
    const parts = dateStr.split(' ');
    if (parts.length >= 2) {
      return {
        month: parts[0].slice(0, 3).toUpperCase(),
        day: parts[1].replace(',', ''),
      };
    }
    return { month: '', day: '' };
  };

  const eventMonth = details?.date
    ? parseDateParts(details.date).month
    : event
    ? parseDateParts(event.date).month
    : '';
  const eventDay = details?.date
    ? parseDateParts(details.date).day
    : event
    ? parseDateParts(event.date).day
    : '';

  const schemas =
    event && details
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            generateEventSchema(
              event.title,
              details.longDescription || details.description || '',
              `${details.date} ${details.time}`,
              event.location,
              `/events/${eventId}`,
              details.endDate,
              event.image,
              details.price,
              details.organizer,
              details.website,
              details.address,
              'https://schema.org/EventScheduled'
            ),
            generateBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Events', url: '/events' },
              { name: event.title, url: `/events/${eventId}` },
            ]),
          ],
        }
      : undefined;

  useSEO({
    title: event
      ? `${event.title} | Roseville Events`
      : 'Event Details | Roseville.org',
    description: details?.description || event?.description || 'Discover upcoming events in Roseville, California.',
    keywords: event
      ? `${event.title}, ${event.category}, Roseville events, Roseville California`
      : 'Roseville events, things to do Roseville',
    canonical: `/events/${eventId}`,
    ogType: 'event',
    ogImage: event?.image,
    ogImageAlt: event?.title,
    schema: schemas,
  });

  if (!event || !details) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-4">Event not found.</p>
          <Link to="/events" className="text-teal-600 font-semibold hover:underline">
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  const admission = details.price || event.price || 'Free';
  const description = details.longDescription || details.description || event.description;
  const tags: string[] = details.tags || [];
  const amenities: string[] = details.amenities || [];
  const gallery: string[] = details.gallery || [];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-[460px] w-full overflow-hidden">
        <img
          src={details.image || event.image}
          alt={event.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />

        {/* Date Badge */}
        <div className="absolute top-6 left-6 bg-white rounded-2xl px-4 py-3 text-center shadow-lg">
          <p className="text-teal-600 text-xs font-bold uppercase tracking-widest">{eventMonth}</p>
          <p className="text-gray-900 text-3xl font-extrabold leading-none">{eventDay}</p>
        </div>

        {/* Category Badge */}
        <div className="absolute top-6 right-6">
          <span className="bg-teal-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full uppercase tracking-wide">
            {event.category}
          </span>
        </div>

        {/* Title */}
        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-5xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
            {event.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {details.time || event.time}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-line"></i>
              {event.location}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-ticket-line"></i>
              {admission}
            </span>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-teal-600 transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <Link to="/events" className="hover:text-teal-600 transition-colors">Events</Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <span className="text-gray-700 font-medium truncate max-w-xs">{event.title}</span>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-0">
            {/* About */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-information-line text-teal-600 text-2xl"></i>
                </div>
                About This Event
              </h2>
              <div className="prose prose-gray max-w-none">
                {description.split('\n\n').map((para: string, idx: number) => (
                  <p key={idx} className="text-gray-700 leading-relaxed mb-4 text-base">
                    {para}
                  </p>
                ))}
              </div>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div className="mb-10">
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="bg-teal-50 text-teal-700 text-xs font-medium px-3 py-1.5 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities */}
            {amenities.length > 0 && (
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-teal-600 text-2xl"></i>
                  </div>
                  Amenities &amp; Features
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {amenities.map((item: string) => (
                    <div
                      key={item}
                      className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-700"
                    >
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-teal-500 font-bold"></i>
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photo Gallery */}
            {gallery.length > 0 && (
              <PhotoGalleryCarousel images={gallery} eventTitle={event.title} />
            )}

            {/* Map */}
            <EventMap
              address={details.address || `${event.location}, Roseville, CA`}
              locationName={event.location}
            />

            {/* Reminder Signup */}
            <EventReminderSignup
              eventTitle={event.title}
              eventDate={details.date || event.date}
              eventMonth={eventMonth}
              eventDay={eventDay}
            />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-5">
            {/* Quick Info Card */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-teal-600 px-5 py-4">
                <p className="text-white font-semibold text-sm uppercase tracking-wide">Event Details</p>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                    <i className="ri-calendar-event-line text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Date</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">
                      {eventMonth} {eventDay}
                      {details.endDate ? ` – ${parseDateParts(details.endDate).month} ${parseDateParts(details.endDate).day}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Time</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">{details.time || event.time}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Location</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">{event.location}</p>
                    {details.address && (
                      <p className="text-xs text-gray-500 mt-0.5">{details.address}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                    <i className="ri-ticket-line text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Admission</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">{admission}</p>
                  </div>
                </div>
                {details.organizer && (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                      <i className="ri-group-line text-teal-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Organizer</p>
                      <p className="text-sm font-semibold text-gray-800 mt-0.5">{details.organizer}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Card */}
            {(details.phone || details.contact || details.website) && (
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 space-y-3">
                <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Contact</h3>
                {details.phone && (
                  <a
                    href={`tel:${details.phone}`}
                    className="flex items-center gap-3 text-sm text-gray-700 hover:text-teal-600 transition-colors"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg">
                      <i className="ri-phone-line text-teal-600"></i>
                    </div>
                    {details.phone}
                  </a>
                )}
                {details.contact && (
                  <a
                    href={`mailto:${details.contact}`}
                    className="flex items-center gap-3 text-sm text-gray-700 hover:text-teal-600 transition-colors"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg">
                      <i className="ri-mail-line text-teal-600"></i>
                    </div>
                    <span className="truncate">{details.contact}</span>
                  </a>
                )}
                {details.website && (
                  <a
                    href={details.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-sm text-teal-600 hover:text-teal-700 transition-colors font-medium"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg">
                      <i className="ri-external-link-line text-teal-600"></i>
                    </div>
                    Visit Website
                  </a>
                )}
              </div>
            )}

            {/* Print Flyer */}
            <PrintEventFlyerButton
              event={{
                id: event.id,
                title: event.title,
                image: details.image || event.image,
                category: event.category,
                month: eventMonth,
                day: eventDay,
                time: details.time || event.time,
                location: event.location,
              }}
              details={details}
            />

            {/* Back to Events */}
            <Link
              to="/events"
              className="flex items-center justify-center gap-2 w-full border border-gray-200 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-50 transition-colors text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              All Events
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
